import React, { useState } from 'react';
import {
  Box,
  Paper,
  Button,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Chip,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import {
  Download as DownloadIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useAmplitude } from '../hooks/useAmplitude';
import api from '../utils/api';

export const PromptToImageUnfiltered: React.FC = () => {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { track } = useAmplitude();
  const isRTL = currentLang === 'he';

  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageId, setImageId] = useState<string | null>(null);
  
  // Styling state
  const [style, setStyle] = useState<string>('');
  const [effectColor, setEffectColor] = useState<string>('');
  const [effectLightning, setEffectLightning] = useState<string>('');
  const [effectFraming, setEffectFraming] = useState<string>('');
  const [colors, setColors] = useState<Array<{ color: string; weight: number }>>([]);
  const [newColorHex, setNewColorHex] = useState<string>('#FF0000');
  const [newColorWeight, setNewColorWeight] = useState<number>(0.5);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError(t('pages.freeTools.promptToImageUnfiltered.errors.emptyPrompt'));
      return;
    }

    if (!isAuthenticated) {
      setError(t('pages.freeTools.promptToImageUnfiltered.errors.loginRequired'));
      setTimeout(() => {
        navigate('/login', { state: { from: '/free-tools' } });
      }, 2000);
      return;
    }

    setLoading(true);
    setError(null);
    setGeneratedImage(null);

    try {
      // Build styling object
      const styling: any = {};
      if (style) styling.style = style;
      
      if (effectColor || effectLightning || effectFraming) {
        styling.effects = {};
        if (effectColor) styling.effects.color = effectColor;
        if (effectLightning) styling.effects.lightning = effectLightning;
        if (effectFraming) styling.effects.framing = effectFraming;
      }
      
      if (colors.length > 0) {
        styling.colors = colors;
      }

      const response = await api.post('/generated-images/generate-unfiltered', {
        prompt: prompt.trim(),
        negativePrompt: negativePrompt.trim() || undefined,
        styling: Object.keys(styling).length > 0 ? styling : undefined,
      });

      if (response.data.success) {
        const { imageBase64 } = response.data.data;
        setGeneratedImage(imageBase64);
        setImageId(null);
        setError(null);
        
        // Track generation event
        track('Prompt to Image Unfiltered - Generated', {
          prompt_length: prompt.length,
          has_image: !!imageBase64,
        });
      } else {
        throw new Error(response.data.error || 'Failed to generate image');
      }
    } catch (err: any) {
      console.error('Error generating image:', err);
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        t('pages.freeTools.promptToImageUnfiltered.errors.generationFailed');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPNG = async () => {
    if (!generatedImage) return;

    try {
      // Convert base64 to blob (handle both with and without data URI prefix)
      let base64Data = generatedImage;
      if (base64Data.includes(',')) {
        base64Data = base64Data.split(',')[1];
      }
      
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cricut-image-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Track download event
      track('Prompt to Image Unfiltered - Download PNG', {
        image_id: imageId,
      });
    } catch (err) {
      console.error('Error downloading PNG:', err);
      setError(t('pages.freeTools.promptToImageUnfiltered.errors.downloadFailed'));
    }
  };

  const handleDownloadSVG = async () => {
    if (!generatedImage) return;

    try {
      // Convert base64 image to SVG
      // First, create an image element from the base64
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      // Ensure base64 has data URI prefix for image src
      let imageSrc = generatedImage;
      if (!imageSrc.startsWith('data:')) {
        imageSrc = `data:image/png;base64,${imageSrc}`;
      }
      
      await new Promise<void>((resolve, reject) => {
        img.onload = () => {
          // Convert to SVG by embedding the image
          const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${img.width}" height="${img.height}" viewBox="0 0 ${img.width} ${img.height}">
  <image width="${img.width}" height="${img.height}" xlink:href="${imageSrc}"/>
</svg>`;

          // Create download link
          const blob = new Blob([svgContent], { type: 'image/svg+xml' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `cricut-image-${Date.now()}.svg`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          resolve();
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = imageSrc;
      });

      // Track download event
      track('Prompt to Image Unfiltered - Download SVG', {
        image_id: imageId,
      });
    } catch (err) {
      console.error('Error downloading SVG:', err);
      setError(t('pages.freeTools.promptToImageUnfiltered.errors.downloadFailed'));
    }
  };

  return (
    <Box sx={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ textAlign: isRTL ? 'right' : 'left' }}>
          {t('pages.freeTools.promptToImageUnfiltered.title')}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 3,
            textAlign: isRTL ? 'right' : 'left',
            whiteSpace: 'pre-line',
          }}
        >
          {t('pages.freeTools.promptToImageUnfiltered.description')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={t('pages.freeTools.promptToImageUnfiltered.promptPlaceholder')}
            disabled={loading}
            sx={{
              mb: 2,
              '& .MuiInputBase-input': {
                direction: 'rtl',
                textAlign: 'right',
              },
            }}
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            value={negativePrompt}
            onChange={(e) => setNegativePrompt(e.target.value)}
            placeholder={t('pages.freeTools.promptToImageUnfiltered.negativePromptPlaceholder')}
            disabled={loading}
            sx={{
              mb: 2,
              '& .MuiInputBase-input': {
                direction: 'rtl',
                textAlign: 'right',
              },
            }}
          />
          
          {/* Styling Options */}
          <Typography variant="h6" sx={{ mt: 3, mb: 2, textAlign: isRTL ? 'right' : 'left' }}>
            {t('pages.freeTools.promptToImageUnfiltered.styling.title')}
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* Style */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>{t('pages.freeTools.promptToImageUnfiltered.styling.style')}</InputLabel>
                <Select
                  value={style}
                  onChange={(e) => setStyle(e.target.value)}
                  label={t('pages.freeTools.promptToImageUnfiltered.styling.style')}
                  disabled={loading}
                >
                  <MenuItem value="">{t('pages.freeTools.promptToImageUnfiltered.styling.none')}</MenuItem>
                  <MenuItem value="photo">Photo</MenuItem>
                  <MenuItem value="digital-art">Digital Art</MenuItem>
                  <MenuItem value="3d">3D</MenuItem>
                  <MenuItem value="painting">Painting</MenuItem>
                  <MenuItem value="low-poly">Low Poly</MenuItem>
                  <MenuItem value="pixel-art">Pixel Art</MenuItem>
                  <MenuItem value="anime">Anime</MenuItem>
                  <MenuItem value="cyberpunk">Cyberpunk</MenuItem>
                  <MenuItem value="comic">Comic</MenuItem>
                  <MenuItem value="vintage">Vintage</MenuItem>
                  <MenuItem value="cartoon">Cartoon</MenuItem>
                  <MenuItem value="vector">Vector</MenuItem>
                  <MenuItem value="studio-shot">Studio Shot</MenuItem>
                  <MenuItem value="dark">Dark</MenuItem>
                  <MenuItem value="sketch">Sketch</MenuItem>
                  <MenuItem value="mockup">Mockup</MenuItem>
                  <MenuItem value="2000s-pone">2000s Phone</MenuItem>
                  <MenuItem value="70s-vibe">70s Vibe</MenuItem>
                  <MenuItem value="watercolor">Watercolor</MenuItem>
                  <MenuItem value="art-nouveau">Art Nouveau</MenuItem>
                  <MenuItem value="origami">Origami</MenuItem>
                  <MenuItem value="surreal">Surreal</MenuItem>
                  <MenuItem value="fantasy">Fantasy</MenuItem>
                  <MenuItem value="traditional-japan">Traditional Japan</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Effect Color */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>{t('pages.freeTools.promptToImageUnfiltered.styling.effectColor')}</InputLabel>
                <Select
                  value={effectColor}
                  onChange={(e) => setEffectColor(e.target.value)}
                  label={t('pages.freeTools.promptToImageUnfiltered.styling.effectColor')}
                  disabled={loading}
                >
                  <MenuItem value="">{t('pages.freeTools.promptToImageUnfiltered.styling.none')}</MenuItem>
                  <MenuItem value="b&w">Black & White</MenuItem>
                  <MenuItem value="pastel">Pastel</MenuItem>
                  <MenuItem value="sepia">Sepia</MenuItem>
                  <MenuItem value="dramatic">Dramatic</MenuItem>
                  <MenuItem value="vibrant">Vibrant</MenuItem>
                  <MenuItem value="orange&teal">Orange & Teal</MenuItem>
                  <MenuItem value="film-filter">Film Filter</MenuItem>
                  <MenuItem value="split">Split</MenuItem>
                  <MenuItem value="electric">Electric</MenuItem>
                  <MenuItem value="pastel-pink">Pastel Pink</MenuItem>
                  <MenuItem value="gold-glow">Gold Glow</MenuItem>
                  <MenuItem value="autumn">Autumn</MenuItem>
                  <MenuItem value="muted-green">Muted Green</MenuItem>
                  <MenuItem value="deep-teal">Deep Teal</MenuItem>
                  <MenuItem value="duotone">Duotone</MenuItem>
                  <MenuItem value="terracotta&teal">Terracotta & Teal</MenuItem>
                  <MenuItem value="red&blue">Red & Blue</MenuItem>
                  <MenuItem value="cold-neon">Cold Neon</MenuItem>
                  <MenuItem value="burgundy&blue">Burgundy & Blue</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Effect Lightning */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>{t('pages.freeTools.promptToImageUnfiltered.styling.effectLightning')}</InputLabel>
                <Select
                  value={effectLightning}
                  onChange={(e) => setEffectLightning(e.target.value)}
                  label={t('pages.freeTools.promptToImageUnfiltered.styling.effectLightning')}
                  disabled={loading}
                >
                  <MenuItem value="">{t('pages.freeTools.promptToImageUnfiltered.styling.none')}</MenuItem>
                  <MenuItem value="studio">Studio</MenuItem>
                  <MenuItem value="warm">Warm</MenuItem>
                  <MenuItem value="cinematic">Cinematic</MenuItem>
                  <MenuItem value="volumetric">Volumetric</MenuItem>
                  <MenuItem value="golden-hour">Golden Hour</MenuItem>
                  <MenuItem value="long-exposure">Long Exposure</MenuItem>
                  <MenuItem value="cold">Cold</MenuItem>
                  <MenuItem value="iridescent">Iridescent</MenuItem>
                  <MenuItem value="dramatic">Dramatic</MenuItem>
                  <MenuItem value="hardlight">Hardlight</MenuItem>
                  <MenuItem value="redscale">Redscale</MenuItem>
                  <MenuItem value="indoor-light">Indoor Light</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Effect Framing */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>{t('pages.freeTools.promptToImageUnfiltered.styling.effectFraming')}</InputLabel>
                <Select
                  value={effectFraming}
                  onChange={(e) => setEffectFraming(e.target.value)}
                  label={t('pages.freeTools.promptToImageUnfiltered.styling.effectFraming')}
                  disabled={loading}
                >
                  <MenuItem value="">{t('pages.freeTools.promptToImageUnfiltered.styling.none')}</MenuItem>
                  <MenuItem value="portrait">Portrait</MenuItem>
                  <MenuItem value="macro">Macro</MenuItem>
                  <MenuItem value="panoramic">Panoramic</MenuItem>
                  <MenuItem value="aerial-view">Aerial View</MenuItem>
                  <MenuItem value="close-up">Close-up</MenuItem>
                  <MenuItem value="cinematic">Cinematic</MenuItem>
                  <MenuItem value="high-angle">High Angle</MenuItem>
                  <MenuItem value="low-angle">Low Angle</MenuItem>
                  <MenuItem value="symmetry">Symmetry</MenuItem>
                  <MenuItem value="fish-eye">Fish-eye</MenuItem>
                  <MenuItem value="first-person">First Person</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Colors */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, textAlign: isRTL ? 'right' : 'left' }}>
              {t('pages.freeTools.promptToImageUnfiltered.styling.colors')} ({colors.length}/5)
            </Typography>
            {colors.map((colorItem, index) => (
              <Chip
                key={index}
                label={`${colorItem.color} (${colorItem.weight})`}
                onDelete={() => {
                  setColors(colors.filter((_, i) => i !== index));
                }}
                sx={{ 
                  mr: 1, 
                  mb: 1,
                  backgroundColor: colorItem.color,
                  color: '#fff',
                  '& .MuiChip-deleteIcon': {
                    color: '#fff',
                  },
                }}
              />
            ))}
            {colors.length < 5 && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  type="color"
                  value={newColorHex}
                  onChange={(e) => setNewColorHex(e.target.value)}
                  disabled={loading}
                  sx={{ width: 100 }}
                />
                <TextField
                  type="number"
                  label={t('pages.freeTools.promptToImageUnfiltered.styling.colorWeight')}
                  value={newColorWeight}
                  onChange={(e) => setNewColorWeight(parseFloat(e.target.value) || 0.5)}
                  inputProps={{ min: 0.05, max: 1, step: 0.05 }}
                  disabled={loading}
                  sx={{ width: 120 }}
                />
                <IconButton
                  onClick={() => {
                    if (newColorWeight >= 0.05 && newColorWeight <= 1) {
                      setColors([...colors, { color: newColorHex, weight: newColorWeight }]);
                      setNewColorHex('#FF0000');
                      setNewColorWeight(0.5);
                    }
                  }}
                  disabled={loading}
                  color="primary"
                >
                  <AddIcon />
                </IconButton>
              </Box>
            )}
          </Box>

          <Button
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
            onClick={handleGenerate}
            disabled={loading || !prompt.trim()}
            sx={{ gap: 1 }}
          >
            {loading ? t('pages.freeTools.promptToImageUnfiltered.generating') : t('pages.freeTools.promptToImageUnfiltered.generateButton')}
          </Button>
        </Box>

        {generatedImage && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom sx={{ textAlign: isRTL ? 'right' : 'left', mb: 2 }}>
                {t('pages.freeTools.promptToImageUnfiltered.success')}
              </Typography>
              <Box
                sx={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  p: 2,
                  backgroundColor: '#fff',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mb: 2,
                  minHeight: '300px',
                }}
              >
                <img
                  src={generatedImage.startsWith('data:') ? generatedImage : `data:image/png;base64,${generatedImage}`}
                  alt="Generated"
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    maxHeight: '500px',
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadSVG}
                  sx={{ gap: 1 }}
                >
                  {t('pages.freeTools.promptToImageUnfiltered.downloadSvg')}
                </Button>
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadPNG}
                  sx={{ gap: 1 }}
                >
                  {t('pages.freeTools.promptToImageUnfiltered.downloadPng')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
      </Paper>
    </Box>
  );
};

